import {
  LoanParams,
  LoanParamsQuery,
  PricingCalculatorLocationState,
  ProjectAssets,
} from './types';
import {
  LoanTypeBackwardsMapping,
  LoanTypeMapping,
  SpecialPaymentProducts,
} from '../../../../../types/api';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface BuildLoanParamsProps {
  values: any;
  rateChangeDate: string | undefined;
  productsByTypeTermPricingGrouping: Record<string, any>;
  selectedInterestRate: string;
  monthlyPayment: number;
  dealerFee: number;
  borrowerAdvanceRate: number;
}

export const buildLoanParams = ({
  values,
  rateChangeDate,
  productsByTypeTermPricingGrouping,
  selectedInterestRate,
  monthlyPayment,
  dealerFee,
  borrowerAdvanceRate,
}: BuildLoanParamsProps) => {
  const pricingGrouping = values.pricingGrouping;
  const isFutureRate = values.pricingGrouping.split('.')[0] === 'future';
  const pricingScenario = Number(values.pricingGrouping.split('.')[1]);
  const productId =
    productsByTypeTermPricingGrouping[
      `${values.loanType}-${values.loanTerm}-${pricingScenario}`
    ]?.id;

  const interestRate = parseFloat(
    Number(selectedInterestRate.slice(0, -1)).toFixed(2)
  );
  let loanParams: LoanParamsQuery = {
    loanSize: values.loanAmount,
    netFunding: values.netFunding,
    isFutureRate: String(isFutureRate),
    loanType: LoanTypeMapping[values.loanType],
    isSpecial: String(SpecialPaymentProducts.includes(values.loanType)),
    // We should only include specialPaymentPercentage if it's defined
    ...(values.specialPaymentPercentage !== undefined && {
      specialPaymentPercentage: values.specialPaymentPercentage,
    }),
    // excel cell is expecting 10/20 format
    loanTerm: `${parseInt(values.loanTerm.slice(0, 2))}%2F${parseInt(
      values.loanTerm.slice(-2)
    )}`,
    pricingScenario,
    pricingGrouping,
    productId,
    rateChangeDate,
    interestRate,
    monthlyPayment,
    ...(values.milestonePayments && {
      milestonePayments: values.milestonePayments,
    }),
    ...(values.milestonePayments === 'Yes' && {
      milestones: JSON.stringify(values.milestones ?? []),
      totalDrawOutstanding: values.totalDrawOutstanding,
      totalInterestIncurred: values.totalInterestIncurred,
    }),
    dealerFee,
    borrowerAdvanceRate,
    projectAssets: JSON.stringify(values.projectAssets),
  };

  const econ = values.economics;
  if (econ.systemLocation && econ.y1saving) {
    loanParams = {
      ...loanParams,
      y1saving: econ.y1saving,
      systemLocation: econ.systemLocation,
    };
  }
  if (econ.y1production && econ.systemSize) {
    loanParams = {
      ...loanParams,
      y1production: econ.y1production,
      systemSize: econ.systemSize,
    };
  }
  if (econ.recInfo) {
    loanParams = { ...loanParams, recInfo: econ.recInfo };
  }

  return loanParams;
};

export const parseLoanTerm = (loanTerm: string) => {
  const decodedLoanTerm = decodeURIComponent(loanTerm);
  const [loanYearsString, amortizationYearsString] = decodedLoanTerm.split('/');
  return {
    loanYears: parseInt(loanYearsString, 10),
    amortizationYears: parseInt(amortizationYearsString, 10),
  };
};

export const generateAndDownloadPDF = async (
  pdfRef: React.RefObject<HTMLElement>
) => {
  if (!pdfRef.current) return;

  // This is handling the PDF generation to always be Desktop wide (due to the table)
  const originalViewport = document
    .querySelector('meta[name="viewport"]')
    ?.getAttribute('content');
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  viewportMeta?.setAttribute('content', 'width=1440, height=3000');

  const element = pdfRef.current;

  const canvas = await html2canvas(element, {
    allowTaint: true,
    useCORS: true,
    scale: 2,
    windowHeight: 3000,
    scrollY: -window.scrollY,
  });
  const data = canvas.toDataURL('image/jpeg'); // Using jpeg format makes the generated PDF way lighter than if using png

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [canvas.width + 96 * 2, canvas.height + 50 * 2],
  });

  pdf.addImage(data, 'PNG', 96, 50, canvas.width, canvas.height);

  pdf.save('Sunstone Credit Loan Pre-Proposal.pdf');
  // Restore the original viewport
  viewportMeta?.setAttribute('content', originalViewport!);
  // Wait for the viewport to adjust back
  await new Promise((resolve) => setTimeout(resolve, 300));
};

export const convertLoanTermToOriginalFormat = (loanTerm: string): string => {
  const { loanYears, amortizationYears } = parseLoanTerm(loanTerm);

  const loanTermPadded = loanYears.toString().padStart(2, '0');
  const amortizationPadded = amortizationYears.toString().padStart(2, '0');

  return `${loanTermPadded}${amortizationPadded}`;
};

// Builds the location.state passed to PriceMyProjectPage when the user clicks
// "Back to Calculator" from LoanDetails. Keeps milestones as an array rather
// than exploding them into 18 flat per-milestone keys.
export const buildCalculatorReturnState = (
  loanParams: LoanParams
): PricingCalculatorLocationState => {
  const {
    loanSize,
    netFunding,
    loanType,
    loanTerm,
    pricingGrouping,
    interestRate,
    specialPaymentPercentage,
    y1saving,
    y1production,
    systemSize,
    systemLocation,
    recInfo,
    isSpecial,
    milestonePayments,
    milestones,
    totalDrawOutstanding,
    totalInterestIncurred,
    projectAssets,
  } = loanParams;

  return {
    loanAmount: loanSize,
    netFunding,
    loanType: LoanTypeBackwardsMapping[loanType].fullStr,
    loanTerm: convertLoanTermToOriginalFormat(loanTerm),
    pricingGrouping,
    interestRate,
    specialPaymentPercentage,
    y1saving,
    y1production,
    systemSize,
    systemLocation,
    recInfo,
    isSpecial,
    ...(milestonePayments && {
      milestonePayments: milestonePayments as 'Yes' | 'No',
    }),
    ...(milestonePayments === 'Yes' && {
      milestones,
      totalDrawOutstanding,
      totalInterestIncurred,
    }),
    projectAssets,
  };
};

// Builds the URL search params object used when navigating from LoanDetails to
// NewBorrowerForm ("Continue to Application"). specialPayment is the computed
// year-1 special payment amount derived from the amortization schedule.
export const buildNewBorrowerParams = (
  loanParams: LoanParams,
  specialPayment?: number
): Record<string, string> => {
  const {
    loanSize,
    productId,
    isSpecial,
    specialPaymentPercentage,
    y1saving,
    systemLocation,
    y1production,
    systemSize,
    recInfo,
    milestonePayments,
    projectAssets,
  } = loanParams;

  const params: Record<string, string | number | undefined> = {
    loanAmount: loanSize,
    productId,
    isSpecial,
  };

  if (isSpecial === 'true') {
    params.specialPayment = specialPayment;
    params.specialPaymentPercentage = specialPaymentPercentage;
  }

  if (y1saving && systemLocation) {
    params.y1saving = y1saving;
    params.systemLocation = systemLocation;
  }

  if (y1production && systemSize) {
    params.y1production = y1production;
    params.systemSize = systemSize;
  }

  if (recInfo) {
    params.recInfo = recInfo;
  }

  if (milestonePayments) {
    params.milestonePayments = milestonePayments;
  }

  if (projectAssets) {
    params.projectAssets = JSON.stringify(projectAssets);
  }

  // Drop undefined / empty values so the URL stays clean.
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => [k, String(v)])
  );
};

// Serializes a parsed LoanParams to a Record<string, string> ready for
// new URLSearchParams(). Handles JSON encoding of milestones[] and projectAssets,
// and omits undefined values so the URL stays clean.
// To add a new field: add it to loanParamsSchema in types.ts, then add one
// line here
export const serializeLoanParamsToUrlQuery = (
  loanParams: LoanParams
): Record<string, string> => {
  const entries: Record<string, string | undefined> = {
    loanSize: String(loanParams.loanSize),
    loanTerm: loanParams.loanTerm,
    pricingScenario: String(loanParams.pricingScenario),
    pricingGrouping: loanParams.pricingGrouping,
    loanType: loanParams.loanType,
    netFunding: String(loanParams.netFunding),
    isFutureRate: String(loanParams.isFutureRate),
    dealerFee: String(loanParams.dealerFee),
    interestRate:
      loanParams.interestRate !== undefined
        ? String(loanParams.interestRate)
        : undefined,
    productId: loanParams.productId,
    rateChangeDate: loanParams.rateChangeDate,
    specialPaymentPercentage:
      loanParams.specialPaymentPercentage !== undefined
        ? String(loanParams.specialPaymentPercentage)
        : undefined,
    y1saving:
      loanParams.y1saving !== undefined
        ? String(loanParams.y1saving)
        : undefined,
    y1production:
      loanParams.y1production !== undefined
        ? String(loanParams.y1production)
        : undefined,
    systemLocation: loanParams.systemLocation,
    systemSize:
      loanParams.systemSize !== undefined
        ? String(loanParams.systemSize)
        : undefined,
    recInfo: loanParams.recInfo,
    isSpecial: loanParams.isSpecial,
    monthlyPayment:
      loanParams.monthlyPayment !== undefined
        ? String(loanParams.monthlyPayment)
        : undefined,
    milestonePayments:
      loanParams.milestonePayments === 'Yes' ||
      loanParams.milestonePayments === 'No'
        ? loanParams.milestonePayments
        : undefined,
    borrowerAdvanceRate:
      loanParams.borrowerAdvanceRate !== undefined
        ? String(loanParams.borrowerAdvanceRate)
        : undefined,
    projectAssets: loanParams.projectAssets
      ? JSON.stringify(loanParams.projectAssets)
      : undefined,
    milestones:
      loanParams.milestonePayments === 'Yes' && loanParams.milestones?.length
        ? JSON.stringify(loanParams.milestones)
        : undefined,
    totalDrawOutstanding:
      loanParams.milestonePayments === 'Yes' &&
      loanParams.totalDrawOutstanding !== undefined
        ? String(loanParams.totalDrawOutstanding)
        : undefined,
    totalInterestIncurred:
      loanParams.milestonePayments === 'Yes' &&
      loanParams.totalInterestIncurred !== undefined
        ? String(loanParams.totalInterestIncurred)
        : undefined,
  };

  return Object.fromEntries(
    Object.entries(entries).filter(([, v]) => v !== undefined && v !== '')
  ) as Record<string, string>;
};

// The subset of LoanParams fields that NewBorrowerForm can modify.
// Used by applyNewBorrowerFormEditsToLoanParams to overlay form state
// onto the base LoanParams that arrived from LoanDetails.
export type NewBorrowerFormEdits = {
  loanSize: number | string;
  netFunding: number;
  loanType: string;
  loanTerm: string;
  pricingGrouping: string;
  pricingScenario: number;
  interestRate?: number;
  y1saving?: number | string | null;
  y1production?: number | string | null;
  systemSize?: number | string | null;
  systemLocation?: string | null;
  recInfo?: string | null;
  specialPaymentPercentage?: number;
  monthlyPayment?: number;
  productId: string;
  dealerFee: number;
  milestonePayments?: 'Yes' | 'No';
  projectAssets?: ProjectAssets;
};

// Merges the fields NewBorrowerForm can change onto the base LoanParams that
// was passed down from LoanDetails. Fields NewBorrowerForm can't edit (milestones[],
// borrowerAdvanceRate, totalDrawOutstanding, totalInterestIncurred) are
// preserved from baseLoanParams so that LoanDetails always has the full
// data needed to render the milestone summary.
export const applyNewBorrowerFormEditsToLoanParams = (
  baseLoanParams: LoanParams,
  formEdits: NewBorrowerFormEdits
): LoanParams => {
  const milestonePayments =
    formEdits.milestonePayments ?? baseLoanParams.milestonePayments;

  return {
    ...baseLoanParams,
    loanSize: Number(formEdits.loanSize),
    netFunding: formEdits.netFunding,
    loanType: formEdits.loanType as LoanParams['loanType'],
    loanTerm: formEdits.loanTerm,
    pricingGrouping: formEdits.pricingGrouping,
    pricingScenario: formEdits.pricingScenario,
    isFutureRate: false,
    interestRate: formEdits.interestRate ?? baseLoanParams.interestRate,
    y1saving:
      formEdits.y1saving != null &&
      formEdits.y1saving !== '' &&
      Number(formEdits.y1saving) > 0
        ? Number(formEdits.y1saving)
        : baseLoanParams.y1saving,
    y1production:
      formEdits.y1production != null &&
      formEdits.y1production !== '' &&
      Number(formEdits.y1production) > 0
        ? Number(formEdits.y1production)
        : baseLoanParams.y1production,
    systemSize:
      formEdits.systemSize != null &&
      formEdits.systemSize !== '' &&
      Number(formEdits.systemSize) > 0
        ? Number(formEdits.systemSize)
        : baseLoanParams.systemSize,
    systemLocation: formEdits.systemLocation ?? baseLoanParams.systemLocation,
    recInfo: formEdits.recInfo ?? baseLoanParams.recInfo,
    specialPaymentPercentage: formEdits.specialPaymentPercentage,
    isSpecial: (formEdits.specialPaymentPercentage ?? 0) > 0 ? 'true' : 'false',
    monthlyPayment: formEdits.monthlyPayment ?? baseLoanParams.monthlyPayment,
    productId: formEdits.productId,
    dealerFee: formEdits.dealerFee,
    milestonePayments,
    projectAssets: formEdits.projectAssets ?? baseLoanParams.projectAssets,
    // Passthrough from baseLoanParams — in NewBorrowerForm UI can't edit these
    milestones:
      milestonePayments === 'Yes' ? baseLoanParams.milestones : undefined,
    totalDrawOutstanding:
      milestonePayments === 'Yes'
        ? baseLoanParams.totalDrawOutstanding
        : undefined,
    totalInterestIncurred:
      milestonePayments === 'Yes'
        ? baseLoanParams.totalInterestIncurred
        : undefined,
  };
};
